import Vue from 'vue'
import { Mixin } from 'vue-mixin-decorator'
import { Route } from 'vue-router'

const queryKeys = [
  'three_d_secure_callback_success',
  'three_d_secure_callback_error_type',
  'three_d_secure_callback_error_code',
  'three_d_secure_callback_error_credit',
  'three_d_secure_callback_error_message',
] as const
type QueryKey = typeof queryKeys[number]

/**
 * クエリから値を取得する処理をどこに置くべきかについては、ミックスインよりも一般的にはルーティング定義で行いPropとして注入する方法のほうが望ましいかもしれない。
 * ただし3D認証の「結果」を一時的・フラッシュメッセージ的に表示するフラグとしてのクエリ、ページ内の動作にもとづいてクエリを消去するという、クエリに対する直接的な関心が存在する。
 * またクエリをPropに注入する方式だと、Propを直接変更できない。
 * このように同じページに留まりつつ可変的なクエリを扱おうとすると、クエリへの依存関係をページコンポーネントないしそのミックスインに直接持たせたほうが依存関係が明示的になると考えられる。
 */
@Mixin
export class ThreeDSecureResultPageMixin extends Vue {
  get threeDSecureCallbackSuccess(): string | null {
    return threeDSecureCallbackResultFromQuery(this.$route.query, 'three_d_secure_callback_success')
  }

  get threeDSecureCallbackErrorType(): string | null {
    return threeDSecureCallbackResultFromQuery(this.$route.query, 'three_d_secure_callback_error_type')
  }

  get threeDSecureCallbackErrorCode(): string | null {
    return threeDSecureCallbackResultFromQuery(this.$route.query, 'three_d_secure_callback_error_code')
  }

  get threeDSecureCallbackErrorCredit(): string | null {
    return threeDSecureCallbackResultFromQuery(this.$route.query, 'three_d_secure_callback_error_credit')
  }

  get threeDSecureCallbackErrorMessage(): string | null {
    return threeDSecureCallbackResultFromQuery(this.$route.query, 'three_d_secure_callback_error_message')
  }

  /**
   * URLから3Dセキュア認証の結果に関するクエリを削除し、ブラウザ履歴もクエリを削除したURLに置き換える。
   *
   * 使用例:
   * このミックスインの利用側では、クエリに応じてポップアップ表示などの副作用を行う想定だが、
   * 画面リロード時にクエリが残っていると、そのクエリによって副作用が発生してしまうため、
   * ポップアップを閉じた時など適切なタイミングでクエリを消去する。
   */
  removeTheeDSecureResultQueryParams(): void {
    const removeKeys: QueryKey[] = Object.values(queryKeys)

    const url = new URL(window.location.href)
    removeKeys.forEach(key => url.searchParams.delete(key))
    window.history.replaceState({}, document.title, url.toString())
  }
}

function typeSafeQuery(query: Pick<Route, 'query'>['query']): Record<QueryKey, string | string[] | undefined> {
  return query as Record<QueryKey, string | string[] | undefined>
}

function threeDSecureCallbackResultFromQuery(
  query: Pick<Route, 'query'>['query'],
  key: QueryKey
): string | null {
  const raw = typeSafeQuery(query)[key]

  if (!raw) return null
  if (typeof raw !== 'string') throw new Error('three_d_secure_callback_error_type query is not string')

  return raw
}

/**
 * @returns 表示すべきエラー情報がない（=成功の）場合はnullを返す
 */
export class CallbackErrorMessageParse {
  parse(
    type: string | null,
    code: string | null,
    credit: string | null,
    _message: string | null
  ): string | null {
    if (type === null) return null

    if (type === 'kuroneko' && code === 'A092000004') {
      return '3Dセキュア認証がキャンセルされたためNGとなりました（コード: A092000004）。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
    }

    if (type === 'kuroneko' && code === 'A092000005') {
      return [
        '3Dセキュア認証が与信のためNGとなりました（コード: A092000005）。カード情報をご確認ください。',
        !!credit ? `カード会社にお問い合わせする際は 与信結果エラーコード ${credit} をお伝えください。` : '',
        '※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください',
      ].join('<br/>')
    }

    return '3Dセキュア認証中にエラーが発生しました。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
  }
}
